import {Page} from 'components/system/Page'
import {useConfirmOrderMutation} from 'hooks/ventrata/useConfirmOrderMutation'
import {some} from 'lodash'
import {useCurrency, useOrderPath} from 'providers/EnvironmentProvider'
import {useOrderDetails} from 'providers/OrderDetailsProvider'
import {OrderProvider} from 'providers/OrderProvider'
import {useResultSetter} from 'providers/ResultProvider'
import {useEffect, useState} from 'react'
import {ga4} from 'utils/ga4'
import {getDiscountDetails} from 'utils/getDiscountDetails'
import {getGa4OrderItems} from 'utils/getGa4OrderItems'
import {getHumanPrice} from 'utils/getHumanPrice'
import {getOrderUnitCounterMap} from 'utils/getOrderUnitCounterMap'
import {singleCall} from 'utils/singleCall'

export const View = () => {
  const order = OrderProvider.useOrder('optional')
  const orderIntent = useOrderDetails()
  const currency = useCurrency()
  const setOrder = OrderProvider.useOrderSetter()
  const setResult = useResultSetter()
  const confirmOrderMutation = useConfirmOrderMutation()
  const confirmOrderMutate = confirmOrderMutation.mutate
  const orderPath = useOrderPath()
  const [delayed, setDelayed] = useState<boolean>(false)
  const [tryAgain, setTryAgain] = useState<boolean>(false)

  useEffect(() => {
    if (!order) {
      return
    }

    setTimeout(() => {
      setDelayed(true)
    }, 5000)

    setTimeout(() => {
      setTryAgain(true)
    }, 15000)

    singleCall('confirmOrder', () => {
      try {
        confirmOrderMutate(
          {order, orderIntent},
          {
            onSuccess(confirmedOrder) {
              setResult({order: confirmedOrder})

              const counterMap = getOrderUnitCounterMap(order)
              const discountDetails = getDiscountDetails(order)

              ga4({
                event: 'purchase',
                ecommerce: {
                  currency,
                  value: getHumanPrice(order.pricing?.retail),
                  checkout_type: 'custom_checkout',

                  tax: 0,
                  shipping: 0,
                  transaction_id: order.supplierReference,
                  coupon: discountDetails ? discountDetails.name : '',
                  payment_type: orderIntent.paymentType,
                  total_tours: order.bookings.length,
                  total_adult: counterMap.ADULT,
                  total_child: counterMap.CHILD,
                  total_infant: counterMap.INFANT,
                  purchase_with_child: counterMap.CHILD > 0 ? 1 : 0,
                  purchase_with_infant: counterMap.INFANT > 0 ? 1 : 0,
                  purchase_with_upgrade: 0,
                  purchase_with_cross: some(orderIntent.crossSelling) ? 1 : 0,
                  items: getGa4OrderItems(order, currency, orderIntent.crossSelling),
                  customer: [
                    {
                      first_name: confirmedOrder.contact.firstName,
                      last_name: confirmedOrder.contact.lastName,
                      email: confirmedOrder.contact.emailAddress,
                      mobile: confirmedOrder.contact.phoneNumber,
                      country: confirmedOrder.contact.country,
                      postal_code: null,
                      type: 'Order',
                    },
                  ],
                },
              })
              setOrder(null)
              window.location.replace(orderPath)
            },
            onError(error) {
              setResult({order, error})
              setOrder(null)
              window.location.replace(orderPath)
            },
          }
        )
      } catch (error) {
        setResult({order, error})
        setOrder(null)
        window.location.replace(orderPath)
      }
    })
  }, [order, setOrder, orderIntent, confirmOrderMutate, setResult, orderPath, currency])

  const retryConfirmation = () => {
    window.location.reload()
  }

  return (
    <Page className="w-full d:w-[1220px] mx-auto px-4 d:px-0">
      <div className="z-[50000] relative block bg-white max-w-full p-8 rounded-xl shadow-md">
        <div className="font-extrabold text-[32px]">Confirming your booking...</div>

        {delayed && (
          <div className="mt-10 text-red-700 text-lg text-center flex flex-col items-center">
            <p className="text-red-700 font-bold">Sorry! Confirming your booking is taking longer than expected.</p>

            {tryAgain ? (
              <>
                <p className="text-sm text-gray-700">
                  You can try again below - don't worry, you will not be charged again.
                </p>
                <button
                  onClick={retryConfirmation}
                  className="mt-4 bg-green-600 block px-4 py-2 text-white rounded-md align-middle"
                >
                  Try again
                </button>
              </>
            ) : (
              <p className="text-sm text-gray-700">
                This can happen during busy times, please wait a few more seconds.
              </p>
            )}
          </div>
        )}
      </div>
    </Page>
  )
}
